import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { ArrowLeft, Save, Send, Archive, Star, ExternalLink } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AUDIENCES = ["ALL_MEMBERS", "CHAPTER_ONLY", "FOUNDING_MEMBERS", "INVITED"];

const statusStyle = (s) => {
  if (s === "PUBLISHED") return "text-lv-active bg-lv-active/10 border-lv-active/20";
  if (s === "EXPIRED") return "text-lv-inactive bg-lv-inactive/10 border-lv-inactive/20";
  return "text-amber-400 bg-amber-400/10 border-amber-400/20";
};

const LINKED_TYPES = ["EVENT", "GAME"];

export default function ContentEdit() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: content, isLoading } = useQuery({ queryKey: ["admin-content-item", id], queryFn: () => api.get(`/admin/content/${id}`) });
  const { data: chapters = [] } = useQuery({ queryKey: ["chapters"], queryFn: () => api.get("/chapters") });

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content && !form) {
      setForm({
        title: content.title || "",
        subtitle: content.subtitle || "",
        body: content.body || "",
        image_url: content.image_url || "",
        chapter_id: content.chapter_id || "",
        audience: content.audience || "ALL_MEMBERS",
        publish_date: content.publish_date || "",
        expiration_date: content.expiration_date || "",
        is_featured: content.is_featured || false,
      });
    }
  }, [content, form]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-content-item", id] }); qc.invalidateQueries({ queryKey: ["admin-content"] }); };

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/content/${id}`, {
        ...form,
        chapter_id: form.chapter_id || null,
        image_url: form.image_url || null,
        publish_date: form.publish_date || null,
        expiration_date: form.expiration_date || null,
        subtitle: form.subtitle || null,
        body: form.body || null,
      });
      toast.success("Content updated");
      invalidate();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const transitionStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/content/${id}`, { status: newStatus });
      toast.success(`Content ${newStatus.toLowerCase()}`);
      setForm(null);
      invalidate();
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading || !form) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;

  const isLinked = LINKED_TYPES.includes(content.content_type);

  return (
    <div data-testid="content-edit-page">
      <Link to="/admin/content" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]">
        <ArrowLeft size={16} /> Content
      </Link>

      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide" data-testid="content-edit-title">{content.title}</h1>
          <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${statusStyle(content.status)}`}>{content.status}</span>
          <span className="text-[9px] uppercase tracking-wider text-lv-muted/60 border border-lv-border/40 px-2 py-0.5">{content.content_type.replace(/_/g, " ")}</span>
        </div>
        <div className="flex gap-2">
          {content.status === "DRAFT" && (
            <button onClick={() => transitionStatus("PUBLISHED")} className="lv-btn-primary inline-flex items-center gap-2 text-xs min-h-[36px] px-4" data-testid="publish-content-btn">
              <Send size={14} /> Publish
            </button>
          )}
          {content.status === "PUBLISHED" && (
            <button onClick={() => transitionStatus("EXPIRED")} className="lv-btn-secondary inline-flex items-center gap-2 text-xs min-h-[36px] px-4" data-testid="expire-content-btn">
              <Archive size={14} /> Expire
            </button>
          )}
        </div>
      </div>

      {/* Source reference card for linked types */}
      {content.source_event && (
        <div className="bg-walnut border border-lv-border/60 p-4 mb-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-2">Linked Event (canonical source)</p>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-cream">{content.source_event.title}</p>
              <p className="text-xs text-lv-muted">{content.source_event.date} {content.source_event.location && `- ${content.source_event.location}`}</p>
            </div>
            <Link to={`/admin/events/${content.source_event_id}`} className="text-bronze hover:text-cream text-xs inline-flex items-center gap-1 min-h-[32px]">
              <ExternalLink size={12} /> Edit Event
            </Link>
          </div>
        </div>
      )}

      <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
        <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-3">Editorial Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Title</label><input value={form.title} onChange={(e) => update("title", e.target.value)} className="lv-input" data-testid="edit-content-title" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Subtitle</label><input value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} className="lv-input" /></div>
          {!isLinked && (
            <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Body</label><textarea value={form.body} onChange={(e) => update("body", e.target.value)} className="lv-input min-h-[120px]" rows={5} /></div>
          )}
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Audience</label>
            <Select value={form.audience} onValueChange={(v) => update("audience", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{AUDIENCES.map((a) => <SelectItem key={a} value={a} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Chapter</label>
            <Select value={form.chapter_id} onValueChange={(v) => update("chapter_id", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue placeholder="All chapters" /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{chapters.map((ch) => <SelectItem key={ch.id} value={ch.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{ch.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Image URL</label><input value={form.image_url} onChange={(e) => update("image_url", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Publish Date</label><input type="date" value={form.publish_date} onChange={(e) => update("publish_date", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Expiration Date</label><input type="date" value={form.expiration_date} onChange={(e) => update("expiration_date", e.target.value)} className="lv-input" /></div>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" onClick={() => update("is_featured", !form.is_featured)} className={`relative w-10 h-5 rounded-full transition-colors ${form.is_featured ? "bg-bronze" : "bg-walnut-deep border border-lv-border"}`} data-testid="edit-content-featured-toggle">
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-cream transition-transform ${form.is_featured ? "translate-x-5" : ""}`} />
          </button>
          <label className="text-xs text-lv-muted tracking-wide">Featured Content</label>
        </div>

        <div className="text-xs text-lv-muted/60 space-y-0.5">
          {content.created_at && <p>Created: {new Date(content.created_at).toLocaleDateString()}</p>}
          {content.updated_at && <p>Updated: {new Date(content.updated_at).toLocaleDateString()}</p>}
        </div>

        <button onClick={saveContent} disabled={saving} className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="save-content-btn">
          <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
